// AuthProvider — session state + login flow for static-login mode.
//
// [BR-09] The session token is NO LONGER held in JS-readable storage. On login the
// server sets an HttpOnly, Secure, SameSite=Strict cookie (__Host-br_session); the
// browser attaches it automatically on every same-origin API call, so the SPA never
// touches the token. Auth state is therefore decided by GET /api/v1/whoami (cookie
// auto-sent), not by a token in localStorage.
//
// `token` is kept on the context (always null now) so the existing api modules —
// which do `if (token) set Authorization: Bearer` — simply send no bearer and let
// the cookie carry auth; the server middleware falls through to the cookie when no
// (or an empty) bearer is present. Programmatic clients (device creds, CLI) still
// send a real Bearer and are unaffected.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** The caller's own identity, from GET /api/v1/whoami. */
export interface Identity {
  subject: string;
  groups: string[];
  /** Runtime feature flag (server env BLUEROCK_MCP_SCAN). Gates whether the UI
   *  shows the MCP Scan nav item + route. Only rc38 (the host holding the
   *  trust-scan key) runs with this on; brdemo runs with it off. */
  mcpScanEnabled: boolean;
}

interface AuthState {
  /** Always null now — the session is an HttpOnly cookie, not a JS token. Typed
   *  string|null (not the literal null) so the api modules that read it for a Bearer
   *  header keep type-checking; the value is null, so they send no bearer and fall
   *  through to cookie auth. */
  token: string | null;
  /** True once whoami has confirmed a live session (cookie present + valid). Guards
   *  gate on THIS, not on a token. */
  authenticated: boolean;
  /** The caller's own identity, or null when not authenticated / not yet resolved. */
  identity: Identity | null;
  /** True until the first whoami resolves, so guards show a spinner instead of
   *  bouncing to /login on a half-known session (a refresh would otherwise flash
   *  "not authenticated" before the cookie check completes). */
  identityLoading: boolean;
  /** "admin" | "viewer" | null. Derived from groups; the server is still the
   *  authority — this only decides what the UI OFFERS. */
  role: string | null;
  /** Runtime MCP-scan feature flag from whoami. */
  mcpScanEnabled: boolean;
  /** The account owes a password rotation (identity carries only `pwreset`). */
  mustChangePassword: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Re-read whoami — call after a rotation so the new role applies without a
   *  re-login (the cookie/token is already promoted server-side). */
  refreshIdentity: () => Promise<void>;
  /** Called by the API client on any 401 — drops auth state so the router
   *  redirects to /login on the next render. (The dead cookie is cleared on the
   *  next logout or simply expires; a 401 means it is no longer honored.) */
  onUnauthorized: () => void;
}

const GROUP_PASSWORD_RESET = "pwreset";

const Ctx = createContext<AuthState | null>(null);

const API_BASE = `${import.meta.env.BASE_URL}api/v1`;

/** GET /api/v1/whoami relying on the session cookie (same-origin → auto-sent; no
 *  Authorization header). Returns the identity on 200, else null. A network error
 *  is treated as "unknown" (null), never a crash. */
async function fetchWhoami(): Promise<Identity | null> {
  try {
    const res = await fetch(`${API_BASE}/whoami`);
    if (!res.ok) return null;
    const body = (await res.json()) as {
      subject?: string;
      groups?: string[];
      mcp_scan_enabled?: boolean;
    };
    return {
      subject: body.subject ?? "",
      groups: body.groups ?? [],
      mcpScanEnabled: body.mcp_scan_enabled === true,
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);

  // On mount, resolve the session from the cookie via whoami.
  useEffect(() => {
    // [BR-09] Purge any legacy JS-readable token left by a pre-cookie build, so an
    // upgraded browser doesn't keep a stale token in localStorage. Harmless if absent.
    try {
      localStorage.removeItem("bluerock.token");
    } catch {
      // storage may be unavailable (private mode); nothing to clean up then.
    }
    let cancelled = false;
    void fetchWhoami().then((id) => {
      if (cancelled) return;
      setIdentity(id);
      setAuthenticated(id !== null);
      setAuthResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshIdentity = useCallback(async () => {
    const id = await fetchWhoami();
    if (id) {
      setIdentity(id);
      setAuthenticated(true);
    }
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const res = await fetch(`${API_BASE}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      let detail = `Login failed (${res.status})`;
      try {
        const body = await res.json();
        if (typeof body.detail === "string") detail = body.detail;
      } catch {
        // body wasn't JSON; keep the status-code message
      }
      throw new Error(detail);
    }
    // The server set the HttpOnly session cookie on this response; we deliberately
    // do NOT read or store the token from the body. Resolve identity via whoami
    // (the cookie is now attached automatically) before reporting success, so the
    // first protected render already knows the role.
    const id = await fetchWhoami();
    setIdentity(id);
    setAuthenticated(id !== null);
    setAuthResolved(true);
    if (id === null) {
      throw new Error("Login succeeded but the session could not be established");
    }
  }, []);

  const signOut = useCallback(() => {
    // Clear the cookie server-side (it is HttpOnly — JS cannot delete it). Drop
    // local state regardless of the request outcome.
    void fetch(`${API_BASE}/logout`, { method: "POST" }).catch(() => {});
    setAuthenticated(false);
    setIdentity(null);
  }, []);

  const onUnauthorized = useCallback(() => {
    setAuthenticated(false);
    setIdentity(null);
    setAuthResolved(true);
  }, []);

  const groups = identity?.groups ?? [];
  const mustChangePassword = groups.includes(GROUP_PASSWORD_RESET);
  const role = mustChangePassword
    ? null
    : (groups.find((g) => g === "admin" || g === "viewer") ?? null);
  const mcpScanEnabled = identity?.mcpScanEnabled ?? false;

  const value = useMemo<AuthState>(
    () => ({
      token: null,
      authenticated,
      identity,
      identityLoading: !authResolved,
      role,
      mcpScanEnabled,
      mustChangePassword,
      signIn,
      signOut,
      refreshIdentity,
      onUnauthorized,
    }),
    [
      authenticated,
      identity,
      authResolved,
      role,
      mcpScanEnabled,
      mustChangePassword,
      signIn,
      signOut,
      refreshIdentity,
      onUnauthorized,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
